import { Link } from '@mui/material';
import { t } from 'i18next';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState, PrefilledDataConfig } from '../../../common/controllers/PaginatedTableController';
import { DefaultHeaderConfiguration, FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportKey } from '../models/ReportKey';
import { OrganizationKeyService } from '../services/OrganizationKeysService';

type State = PaginatedTableState & {};

type Props = {
  keyId: number;
  initialData?: PaginationResult<ReportKey> | null;
  parentLoading?: boolean;
  withPrefill?: boolean;
};

export const DEFAULT_MATCHED_KEYS_PAGE_SIZE = 10;

export class MatchedKeysTable extends PaginatedTableController<ReportKey, Props, State> {
  private readonly keyService: OrganizationKeyService;

  static defaultProps = {
    initialData: null,
    parentLoading: false,
    withPrefill: false,
  };

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.keyService = new OrganizationKeyService();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.keyService.cancelAllRequests();
  }

  protected getPrefilledConfig() {
    if (!this.props.withPrefill) {
      return super.getPrefilledConfig();
    }

    return {
      prefillActive: true,
      initialLoading: this.props.parentLoading,
      initialData: this.props.initialData,
    } as PrefilledDataConfig<ReportKey>;
  }

  protected getDefaultPageSize(): number {
    return DEFAULT_MATCHED_KEYS_PAGE_SIZE;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected getDefaultHeaderConfigurations(): DefaultHeaderConfiguration {
    return { title: t('matchedKeys') };
  }

  protected getFieldConfigurations(): FieldConfiguration<ReportKey>[] {
    return [
      {
        fieldKey: 'id',
        label: t('id'),
        description: t('id'),
        columnProps: {
          maxWidth: 100,
        },
        render: (model, { tabIndex }) => (
          <Link
            tabIndex={tabIndex}
            href={UrlHelper.generatePath(PATHS.REPORT_KEY_LIFE_CYCLE, { reportId: model.getReportId().toString(), keyId: model.getId().toString() })}
            aria-label={
              t('a11y.organization.key.matchedKeys.keyIdLabel', {
                keyId: model.getId(),
                reportId: model.getReportId(),
                reportName: model.getReportName(),
              }) as string
            }
          >
            {model.getId()}
          </Link>
        ),
      },
      {
        fieldKey: 'projectName',
        label: t('project'),
        description: t('project'),
        render: (model, { tabIndex, hasFocus }) => {
          const project = model.getProject();
          if (project === null) return <BlankCell blankText={t('noProject')} />;
          return (
            <Link
              tabIndex={tabIndex}
              href={UrlHelper.generatePath(PATHS.PROJECT_DASHBOARD, { projectId: project.getId().toString() })}
              aria-label={t('a11y.organization.key.matchedKeys.projectLabel', { projectId: project.getId(), projectName: project.getName() }) as string}
            >
              <TypographyOverflow hasFocus={hasFocus}>{project.getName()}</TypographyOverflow>
            </Link>
          );
        },
      },
      {
        fieldKey: 'reportName',
        label: t('report'),
        description: t('report'),
        render: (model, { tabIndex, hasFocus }) => {
          const ariaLabel = t('a11y.organization.key.matchedKeys.reportLabel', { reportId: model.getReportId(), reportName: model.getReportName() });
          return (
            <Link tabIndex={tabIndex} href={UrlHelper.generatePath(PATHS.REPORT, { reportId: model.getReportId().toString() })} aria-label={ariaLabel}>
              <TypographyOverflow hasFocus={hasFocus}>{model.getReportName()}</TypographyOverflow>
            </Link>
          );
        },
      },
      {
        fieldKey: 'operationSummary',
        label: t('usage'),
        description: t('usage'),
        render: (model, { hasFocus }) =>
          model.getOperationSummary() ? (
            <TypographyOverflow hasFocus={hasFocus}>{model.getOperationSummary()}</TypographyOverflow>
          ) : (
            <BlankCell blankText={t('noOperations')} />
          ),
      },
    ];
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<ReportKey>> {
    try {
      return await this.keyService.getMatchedKeys(this.props.keyId, cursorInfo, this.getFilters());
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }
}
